// The canonical Studio control-plane implementation of `MapPersistence`.
//
// Every consumer that stores its map in a Studio project (Studio's own editor
// and any game built on build-kit) shares this one implementation, so autosave
// semantics, the non-prop round-trip and the snapshot shape cannot drift apart.
// Transport and auth stay the host's business: it supplies a `request`
// function, which is the only thing that differs between Studio (session
// cookie/bearer from Amplify) and a game (a dedicated content account).

import { migrateMapDocument } from '../../content/schema/map';
import type {
  MapDecal,
  MapDocument,
  MapLight,
  MapObjectInstance,
  MapRoomMarker,
  MapTriggerVolume,
} from '../../content/schema/map';
import type {
  MapPersistence,
  MapPersistenceResult,
  SaveOptions,
  SnapshotResult,
} from './ProjectPersistence';

/** The non-prop collections that travel alongside `objectInstances`. */
export interface MapNonProps {
  lights?: MapLight[];
  triggers?: MapTriggerVolume[];
  decals?: MapDecal[];
  markers?: MapRoomMarker[];
}

/**
 * The `map_data` document as stored by the control-plane API. It is a superset
 * of `MapDocument`: the structural fields (`floors`, `settings`, …) plus the
 * transient `autosave*` slots the editor writes between explicit saves.
 */
export interface MapWire extends Partial<MapDocument> {
  autosaveInstances?: MapObjectInstance[];
  autosaveLights?: MapLight[];
  autosaveTriggers?: MapTriggerVolume[];
  autosaveDecals?: MapDecal[];
  autosaveMarkers?: MapRoomMarker[];
}

/**
 * Minimal HTTP surface a host must provide. Paths are API-root-relative.
 *
 * Deliberately un-generic: a generic method here would force every host to
 * write a generic implementation just to satisfy assignability. The engine
 * knows the shapes it asked for and narrows them itself.
 */
export interface StudioApiTransport {
  get(path: string): Promise<unknown>;
  put(path: string, body: unknown, opts?: { keepalive?: boolean }): Promise<unknown>;
  post(path: string, body: unknown): Promise<unknown>;
}

function collections(wire: MapWire | null): MapPersistenceResult {
  return {
    objectInstances: wire?.objectInstances ?? [],
    lights: wire?.lights ?? [],
    triggers: wire?.triggers ?? [],
    decals: wire?.decals ?? [],
    markers: wire?.markers ?? [],
  };
}

/**
 * A `MapPersistence` backed by the Studio control-plane API, plus
 * `loadDocument` for hosts that need the whole map (structure included) rather
 * than just the editable collections.
 */
export interface StudioApiPersistence extends MapPersistence {
  /**
   * The full stored document, migrated to the current schema version, or null
   * when the project has no map yet or the request fails. Games call this at
   * boot so players get what was authored, not a bundled snapshot of it.
   */
  loadDocument(projectId: string): Promise<MapDocument | null>;
  /** Persist structural changes (floors/walls/settings), not just instances. */
  saveDocument(projectId: string, document: MapDocument): Promise<void>;
}

export function createStudioApiPersistence(
  transport: StudioApiTransport,
): StudioApiPersistence {
  const mapPath = (projectId: string) => `/projects/${projectId}/map`;

  async function readMap(projectId: string): Promise<MapWire | null> {
    return ((await transport.get(mapPath(projectId))) as MapWire | null) ?? null;
  }

  // saveMap/saveDocument are both GET-modify-PUT against the same map
  // document. Two overlapping calls (the autosave timer firing alongside an
  // explicit Ctrl+S, or a Map Builder structural save racing an Edit-mode
  // prop autosave) would both read the same pre-save document and the
  // later PUT would silently clobber the earlier one's write with stale
  // data. Chaining every read-modify-write through this queue makes each
  // one wait for the previous to fully land before it reads, so it always
  // merges onto the true latest state instead of a stale snapshot.
  let mapWriteQueue: Promise<unknown> = Promise.resolve();
  function serialized<T>(run: () => Promise<T>): Promise<T> {
    const result = mapWriteQueue.then(run, run);
    mapWriteQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  return {
    async loadMap(projectId) {
      return collections(await readMap(projectId));
    },

    async loadDocument(projectId) {
      try {
        const wire = await readMap(projectId);
        // A wire with no floors is not a renderable document - a project whose
        // map has only ever been touched by the prop editor looks like this.
        if (!wire || !Array.isArray(wire.floors) || wire.floors.length === 0) return null;
        return migrateMapDocument(wire as MapDocument);
      } catch {
        return null;
      }
    },

    async saveDocument(projectId, document) {
      return serialized(async () => {
        const current = (await readMap(projectId)) ?? {};
        await transport.put(mapPath(projectId), {
          map_data: { ...current, ...document },
        });
      });
    },

    async saveMap(
      projectId: string,
      instances: MapObjectInstance[],
      options?: SaveOptions,
      nonProps?: MapNonProps,
    ): Promise<MapPersistenceResult> {
      return serialized(async () => {
        const current = (await readMap(projectId)) ?? {};

        // Autosave and an explicit save both write straight to the canonical
        // fields. Nothing in the codebase ever reads the transient autosave*
        // slots back, so stashing autosave data there instead of here used to
        // mean it was silently discarded on reload; clear any leftover legacy
        // slots from before this fix so they don't linger on the document.
        current.objectInstances = instances;
        if (nonProps) {
          if (nonProps.lights) current.lights = nonProps.lights;
          if (nonProps.triggers) current.triggers = nonProps.triggers;
          if (nonProps.decals) current.decals = nonProps.decals;
          if (nonProps.markers) current.markers = nonProps.markers;
        }
        delete current.autosaveInstances;
        delete current.autosaveLights;
        delete current.autosaveTriggers;
        delete current.autosaveDecals;
        delete current.autosaveMarkers;

        await transport.put(mapPath(projectId), { map_data: current }, { keepalive: options?.keepalive });
        return {
          objectInstances: instances,
          lights: nonProps?.lights ?? current.lights ?? [],
          triggers: nonProps?.triggers ?? current.triggers ?? [],
          decals: nonProps?.decals ?? current.decals ?? [],
          markers: nonProps?.markers ?? current.markers ?? [],
        };
      });
    },

    async loadSnapshot(projectId: string, snapshotId?: string): Promise<MapPersistenceResult> {
      if (snapshotId) {
        const wire = (await transport.get(
          `/projects/${projectId}/snapshots/${snapshotId}`,
        )) as MapWire | null;
        return collections(wire);
      }
      const snapshots = (await transport.get(`/projects/${projectId}/snapshots`)) as
        | Array<{ id: string }>
        | null;
      const latestId = snapshots?.[0]?.id;
      if (latestId) {
        const wire = (await transport.get(
          `/projects/${projectId}/snapshots/${latestId}`,
        )) as MapWire | null;
        return collections(wire);
      }
      return this.loadMap(projectId);
    },

    async createSnapshot(
      projectId: string,
      instances: MapObjectInstance[],
      label: string,
      nonProps?: MapNonProps,
    ): Promise<SnapshotResult> {
      const current = (await readMap(projectId)) ?? {};
      current.objectInstances = instances;
      if (nonProps) {
        if (nonProps.lights) current.lights = nonProps.lights;
        if (nonProps.triggers) current.triggers = nonProps.triggers;
        if (nonProps.decals) current.decals = nonProps.decals;
        if (nonProps.markers) current.markers = nonProps.markers;
      }

      const snapshot = (await transport.post(`/projects/${projectId}/snapshots`, {
        name: label,
        map_data: current,
      })) as { id: string; name?: string };
      return { snapshotId: snapshot.id, label: snapshot.name || label };
    },
  };
}
